/**
 * Centralized Permission Helper
 * 
 * Tập trung logic phân quyền để giảm rủi ro khi thay đổi.
 * Code cũ (role === 'admin') vẫn hoạt động bình thường.
 */

// Role constants
export const ROLES = {
    ADMIN: 'admin',
    SENIOR_MANAGER: 'senior_manager',
    MANAGER: 'manager',
    EMPLOYEE: 'employee'
};

// ============================================
// Basic Role Checks
// ============================================

/**
 * Check if user is admin
 */
export const isAdmin = (user) => user?.role === ROLES.ADMIN;

/**
 * Check if user is senior manager
 */
export const isSeniorManager = (user) => user?.role === ROLES.SENIOR_MANAGER;

/**
 * Check if user is manager
 */
export const isManager = (user) => user?.role === ROLES.MANAGER;

/**
 * Check if user is employee
 */
export const isEmployee = (user) => user?.role === ROLES.EMPLOYEE;

// ============================================
// Permission Checks
// ============================================

/**
 * Can manage schedules (add/edit/delete shifts)
 * Admin, Senior Manager: all departments
 * Manager: own department only
 */
export const canManageSchedules = (user) => isAdmin(user) || isSeniorManager(user) || isManager(user);

/**
 * Can view schedule overview (tổng hợp)
 * Admin, Senior Manager: all departments
 * Manager: own department only
 */
export const canViewOverview = (user) => isAdmin(user) || isSeniorManager(user) || isManager(user);

/**
 * Can approve requests (leave, shift changes)
 * Admin, Senior Manager: all requests
 * Manager: own department only
 */
export const canApproveRequests = (user) => isAdmin(user) || isSeniorManager(user) || isManager(user);

/**
 * Can access admin settings pages (/admin, /admin/attendance-system, etc.)
 * Admin and Senior Manager only
 */
export const canAccessAdminPages = (user) => isAdmin(user) || isSeniorManager(user);

/**
 * Can access salary management page
 * Admin only (Senior Manager cannot access)
 */
export const canAccessSalary = (user) => isAdmin(user);

/**
 * Can manage a specific department
 * Admin, Senior Manager: all departments
 * Manager: own department only
 * 
 * @param {Object} user - Current user
 * @param {string} targetDepartment - Department to check
 */
export const canManageDepartment = (user, targetDepartment) => {
    if (!user) return false;
    if (isAdmin(user) || isSeniorManager(user)) return true;
    if (isManager(user) && user.department === targetDepartment) return true;
    return false;
};

/**
 * Can manage a specific user (for schedule editing)
 * Admin, Senior Manager: all users
 * Manager: users in same department
 * 
 * @param {Object} currentUser - Current logged in user
 * @param {Object} targetUser - User to manage (with department field)
 */
export const canManageUser = (currentUser, targetUser) => {
    if (!currentUser) return false;
    if (isAdmin(currentUser) || isSeniorManager(currentUser)) return true;
    if (isManager(currentUser) && currentUser.department === targetUser?.department) return true;
    return false;
};

/**
 * Get departments that user can manage
 * Admin, Senior Manager: returns null (meaning all)
 * Manager: returns [own department]
 * Employee: returns []
 */
export const getManagedDepartments = (user) => {
    if (!user) return [];
    if (isAdmin(user) || isSeniorManager(user)) return null; // null means all
    if (isManager(user) && user.department) return [user.department];
    return [];
};

/**
 * Check if user has any management role (admin, senior_manager, or manager)
 */
export const hasManagementRole = (user) => isAdmin(user) || isSeniorManager(user) || isManager(user);

/**
 * Check if user has full admin-level access (admin or senior_manager)
 * Note: Senior Manager has admin access except salary
 */
export const hasAdminAccess = (user) => isAdmin(user) || isSeniorManager(user);

